// src/components/Bidding/Sale.js
import React, { useContext, useState, useEffect, useRef, useCallback } from 'react';
import { View, Pressable } from 'react-native';
import { useQuery, useSubscription, gql, useApolloClient, useMutation } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { HMSRoomProvider } from '@100mslive/react-sdk';
import Modal from 'src/elements/NativeModal';
import { DateTime } from 'luxon';

import AccountStatus from 'src/components/Account/Status';
import DataState from 'src/components/DataState';
import AuthContext from 'src/contexts/AuthContext';
import * as colors from 'src/styles/colors';
import TimeRemaining from 'src/components/Timer';
import Terms from 'src/components/Pages/Terms';

import {
  Container,
  BorderedContainer,
  Column,
  SmallColumn,
  Title,
  Subtitle,
  NoticeContainer,
  NoticeText,
  Split,
  BlackBackground,
} from './elements';
import NewStream from './NewStream';
import Bids from './Bids';
import Flash from './Flash';
import SaleAssets from './SaleAssets';
import SaleAssetDetail from './SaleAssetDetail';
import Visit from './Visit';
import {
  SALE,
  BIDDING_SALE_ASSETS_WITH_BIDS,
  CONFIRM_TCS,
} from './queries';
import useSubscriptions from './useSubscriptions';
import { Dimensions } from 'react-native-web';
import Accept from '../Sales/Announcements/Accept';

// ------------------------------------
// Subscription: live bidding updates
// ------------------------------------
const BIDDING_UPDATED = gql`
  subscription BiddingUpdated($saleId: ID!) {
    biddingUpdated(saleId: $saleId) {
      saleAssetId
      timestamp
      countdownTimeMs

      highBid
      biddingNext
      biddingNextManual
      biddingStep
      biddingStatus
      biddingNotice
      activeStream

      bids {
        id
        price
        note
        paddleNumber
        paddleService { id name code __typename }
        removedAt
        buyer { id city state cityState __typename }
        backgroundColor
        foregroundColor
        __typename
      }

      countdownTime
      __typename
    }
  }
`;

const BID_PATCH_FRAGMENT = gql`
  fragment BidPatchFields on Bid {
    id
    __typename
    price
    note
    paddleNumber
    removedAt
    paddleService { id name code __typename }
    buyer { id city state cityState __typename }
    backgroundColor
    foregroundColor
  }
`;

const dimensions = Dimensions.get('window');
const smallScreen = dimensions.width < 800;
const mediumScreen = dimensions.width > 480 && dimensions.width < 1024;

// ----------------------------
// Helpers
// ----------------------------

// Patch the SaleAsset entity in Apollo cache so *all* components using it update.
const patchSaleAsset = (cache, saleAssetId, payload) => {
  const saCacheId = cache.identify({ __typename: 'SaleAsset', id: saleAssetId });
  if (!saCacheId) return;

  cache.modify({
    id: saCacheId,
    fields: {
      highBid(existing) {
        return payload.highBid != null ? String(payload.highBid) : existing;
      },

      // ✅ FIX: normalize bids and return REFERENCES (not raw objects)
      bids(existing = []) {
        if (!Array.isArray(payload.bids)) return existing;

        return payload.bids.map((b) => {
          // write/normalize the Bid entity
          cache.writeFragment({
            fragment: BID_PATCH_FRAGMENT,
            data: { __typename: 'Bid', ...b },
          });

          // return a reference to the normalized Bid
          return cache.toReference({ __typename: 'Bid', id: b.id });
        });
      },

      biddingNext(existing) {
        return payload.biddingNext != null ? payload.biddingNext : existing;
      },
      biddingStep(existing) {
        return payload.biddingStep != null ? payload.biddingStep : existing;
      },
      biddingStatus(existing) {
        return payload.biddingStatus != null ? payload.biddingStatus : existing;
      },
      countdownTimeMs(existing) {
        if (typeof payload.countdownTimeMs !== 'number') return existing;

        const asOf = Number(payload.timestamp) || Date.now();
        return Math.max(0, payload.countdownTimeMs - (Date.now() - asOf));
      },

      // viewer-specific; leave alone
      myMaxBidPrice(existing) {
        return existing;
      },
      myMaxBidSurpassed(existing, { readField }) {
        const myMax = readField('myMaxBidPrice');
        if (myMax == null) return existing;
        if (payload.highBid == null) return existing;

        return parseInt(payload.highBid, 10) > parseInt(myMax, 10);
      },
    },
  });
};

// Patch the Sale.biddingSaleAsset pointer so “green/current lot” moves.
const patchSaleBiddingPointer = (cache, saleId, saleAssetId) => {
  const saleCacheId = cache.identify({ __typename: 'Sale', id: saleId });
  if (!saleCacheId) return;

  const ref = cache.toReference({ __typename: 'SaleAsset', id: saleAssetId });
  if (!ref) return;

  cache.modify({
    id: saleCacheId,
    fields: {
      biddingSaleAsset() {
        return ref;
      },
      biddingUpdatedTimestamp(existing) {
        return existing;
      },
    },
  });
};

const BiddingSale = () => {
  const { currentUser } = useContext(AuthContext);
  const { id } = useParams();
  const [showTcs, setShowTcs] = useState(false);

  const [buyerUserApproveTcs] = useMutation(CONFIRM_TCS);

  useEffect(() => {
    if (currentUser?.approvedTcsAt === null) {
      setShowTcs(true);
    } else {
      setShowTcs(false);
    }
  }, [currentUser?.approvedTcsAt]);

  // Existing server-side subs (flash, etc.)
  useSubscriptions({ id });

  const client = useApolloClient();

  const [announcements, setAnnouncements] = useState(false);
  const [announcementAssetDetails, setAnnouncementAssetDetails] = useState(false);

  // Local selection for TIMED sales (ID only so we keep Apollo reactivity)
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  // Tiny tick to force a rerender right after we prime cache on click
  const [selectionTick, setSelectionTick] = useState(0);

  // Latest countdown payload (either from initial query “bootstrap” or live sub)
  const [lastBiddingUpdate, setLastBiddingUpdate] = useState(null);

  // Main sale query
  const { data, refetch } = useQuery(SALE, {
    variables: { id },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  // Catalog list with bids (kept warm)
  const { data: listData, refetch: refetchList } = useQuery(BIDDING_SALE_ASSETS_WITH_BIDS, {
    variables: { saleId: id },
  });

  const sale = data?.sale || null;
  const listAssets = listData?.saleAssets || [];

  // Compute which asset the main panel should render
  const activeSaleAsset =
    sale?.saleType === 'Timed'
      ? (selectedAssetId
          ? listAssets.find((a) => a.id === selectedAssetId) || sale?.biddingSaleAsset || null
          : sale?.biddingSaleAsset || null)
      : (sale?.biddingSaleAsset || null);

  // Track the currently displayed lot id for comparison in the sub handler
  const activeAssetIdRef = useRef(null);
  useEffect(() => {
    activeAssetIdRef.current = activeSaleAsset?.id || null;
  }, [activeSaleAsset?.id]);

  // Keep local selection in sync with sale type / initial active lot
  useEffect(() => {
    if (!sale) return;
    if (sale.saleType !== 'Timed') {
      setSelectedAssetId(null);
    } else {
      if (!selectedAssetId && sale.biddingSaleAsset?.id) {
        setSelectedAssetId(sale.biddingSaleAsset.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sale?.saleType, sale?.biddingSaleAsset?.id]);

  // Helper: jump selection back to the *current* bidding lot (Timed)
  const jumpToCurrentLot = useCallback(() => {
    if (!sale || sale.saleType !== 'Timed') return;

    const currentId = sale?.biddingSaleAsset?.id;
    if (!currentId) return;

    setSelectedAssetId(currentId);

    const ms = sale?.biddingSaleAsset?.countdownTimeMs ?? sale?.countdownTimeMs ?? null;
    if (ms != null) {
      setLastBiddingUpdate({
        saleAssetId: currentId,
        countdownTimeMs: ms,
        timestamp: String(Date.now()),
        __typename: 'BiddingUpdatedPayload',
      });
    }

    setSelectionTick((t) => t + 1);
  }, [sale]);

  // Live countdown + bid-change subscription
  useSubscription(BIDDING_UPDATED, {
    variables: { saleId: id },
    onData: ({ data: subData }) => {
      const payload = subData?.data?.biddingUpdated;
      if (!payload) return;

      // ✅ 0) Move the “current lot” pointer
      if (payload.saleAssetId) {
        patchSaleBiddingPointer(client.cache, id, payload.saleAssetId);
      }

      // ✅ 1) Countdown
      if (typeof payload.countdownTimeMs === 'number') {
        const asOf = Number(payload.timestamp) || Date.now();
        const adjustedMs = Math.max(0, payload.countdownTimeMs - (Date.now() - asOf));
        setLastBiddingUpdate({
          saleAssetId: payload.saleAssetId,
          countdownTimeMs: adjustedMs,
          timestamp: String(Date.now()),
          __typename: 'BiddingUpdatedPayload',
        });
      }

      // ✅ 2) Patch the SaleAsset entity for whichever asset changed
      if (payload.saleAssetId) {
        patchSaleAsset(client.cache, payload.saleAssetId, payload);
      }

      // ✅ 3) Backstop refetch behavior:
      // - if the updated asset is the one we’re showing, refetch SALE
      // - if it’s NOT the active one (catalog row change), refetch the list query
      // This prevents “Chrome doesn’t update for non-active lots” edge cases.
      if (payload.saleAssetId) {
        if (payload.saleAssetId === activeAssetIdRef.current) {
          refetch();
        } else {
          // keep the catalog list in sync even when you’re not viewing that lot
          refetchList();
        }
      }
    },
  });

  // Announcements for currently displayed lot
  useEffect(() => {
    if (activeSaleAsset && currentUser) {
      const anns = activeSaleAsset.asset?.announcements || [];
      const notAccepted = anns.filter((a) => {
        const acceptances = a.acceptances?.map((x) => x.buyerUser.id);
        return !acceptances?.includes(currentUser.id);
      });
      if (notAccepted.length > 0) {
        setAnnouncementAssetDetails(`${activeSaleAsset.correctedLotNumber} | ${activeSaleAsset.asset?.name}`);
        setAnnouncements(notAccepted);
      } else {
        setAnnouncements(false);
      }
    } else {
      setAnnouncements(false);
    }
  }, [activeSaleAsset, currentUser]);

  // Bootstrap/refresh the countdown whenever the displayed active asset changes
  useEffect(() => {
    if (!activeSaleAsset) return;

    if (
      lastBiddingUpdate?.saleAssetId === activeSaleAsset.id &&
      lastBiddingUpdate?.countdownTimeMs != null
    ) {
      return;
    }

    const ms = activeSaleAsset?.countdownTimeMs ?? sale?.countdownTimeMs ?? null;

    if (ms != null) {
      setLastBiddingUpdate({
        saleAssetId: activeSaleAsset.id,
        countdownTimeMs: ms,
        timestamp: String(Date.now()),
        __typename: 'BiddingUpdatedPayload',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSaleAsset?.id, activeSaleAsset?.countdownTimeMs, sale?.countdownTimeMs]);

  /**
   * TIMED events: select by ID; prime cache with row fields so main panel renders instantly.
   */
  const handleSelectSaleAsset = useCallback(
    (clicked) => {
      if (!sale || sale.saleType !== 'Timed' || !clicked?.id) return;

      setSelectedAssetId(clicked.id);

      client.cache.writeFragment({
        id: client.cache.identify({ __typename: 'SaleAsset', id: clicked.id }),
        fragment: gql`
          fragment PrimeSaleAsset on SaleAsset {
            id
            __typename
            correctedLotNumber
            biddingStatus
            highBid
            biddingNext
            biddingStep
            biddingSoldAmount
            pendingSellerConfirmation
            countdownTimeMs
            watching
            pendingBidGroupId
            activeOffer { id __typename buyer { id __typename } }
            bids {
              id
              price
              note
              paddleNumber
              paddleService { id name code __typename }
              removedAt
              buyer { id city state __typename }
              __typename
            }
            asset {
              id
              name
              vin
              cityState
              primaryPhotoUrl
              webPath
              __typename
            }
          }
        `,
        data: { __typename: 'SaleAsset', ...clicked },
      });

      const ms = clicked?.countdownTimeMs ?? null;

      if (ms != null) {
        setLastBiddingUpdate({
          saleAssetId: clicked.id,
          countdownTimeMs: ms,
          timestamp: String(Date.now()),
          __typename: 'BiddingUpdatedPayload',
        });
      }

      setSelectionTick((t) => t + 1);
    },
    [client, sale]
  );

  // ----------------------------
  // Render
  // ----------------------------
  if (!data) {
    return <DataState.Loading />;
  }

  const remainingMsForActiveLot =
    lastBiddingUpdate?.saleAssetId === activeSaleAsset?.id
      ? lastBiddingUpdate?.countdownTimeMs ?? null
      : null;

  const timedShowBackToCurrent =
    sale?.saleType === 'Timed' &&
    sale?.biddingSaleAsset?.id &&
    selectedAssetId &&
    selectedAssetId !== sale.biddingSaleAsset.id;

  const handleTcsSubmit = () => {
    buyerUserApproveTcs({ variables: { approvedTcsAt: DateTime.now().toISO() } });
    setShowTcs(false);
  };

  return (
    <>
      <BlackBackground>
        <Flash sale={sale} />
        <Container style={{ maxHeight: smallScreen || mediumScreen ? 'fit-content' : '640px' }}>
          <Column>
            <BorderedContainer style={{ minHeight: smallScreen ? 'fit-content' : '340px' }}>
              <AccountStatus />

              <div
                style={{
                  display: 'flex',
                  flexDirection: smallScreen || mediumScreen ? 'column' : 'row',
                  gap: smallScreen || mediumScreen ? '20px' : '10px',
                }}
              >
                <Title>{sale.name}</Title>
                {sale.biddingNotice && (
                  <NoticeContainer
                    style={{
                      width: '50%',
                      minWidth: 300,
                      marginRight: 'auto',
                      marginLeft: 'auto',
                      marginBottom: 20,
                      borderRadius: 20,
                    }}
                  >
                    <NoticeText>{sale.biddingNotice || ' '}</NoticeText>
                  </NoticeContainer>
                )}
              </div>

              <View
                style={{
                  flexDirection: smallScreen || mediumScreen ? 'column' : 'row',
                  gap: 20,
                  justifyContent: smallScreen || mediumScreen ? null : 'space-between',
                  alignItems: 'baseline',
                }}
              >
                {sale.bidding ? (
                  <Subtitle
                    style={{
                      backgroundColor: `${colors.elGreen}`,
                      color: '#fff',
                      paddingLeft: '5px',
                      paddingRight: '5px',
                    }}
                  >
                    Sale is Active
                  </Subtitle>
                ) : (
                  <Subtitle
                    style={{
                      backgroundColor: `${colors.elRed}`,
                      color: '#fff',
                      paddingLeft: '5px',
                      paddingRight: '5px',
                    }}
                  >
                    Sale is Not Active
                  </Subtitle>
                )}

                <Subtitle>{sale.lotsRemainingLabel}</Subtitle>

                {timedShowBackToCurrent && (
                  <button
                    onClick={jumpToCurrentLot}
                    style={{
                      padding: '6px 10px',
                      borderRadius: 8,
                      border: '1px solid #000',
                      background: colors.elGreen,
                      color: '#fff',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                    title="Jump back to the currently bidding lot"
                  >
                    Back to Current Lot
                  </button>
                )}

                {sale.saleType === 'Live' && (
                  <HMSRoomProvider>
                    <Visit saleId={id} page="Bidder" />
                    {sale.bidding && (
                      <NewStream
                        userName={
                          currentUser
                            ? `Buyer-${currentUser.id} - ${currentUser.fullName}`
                            : 'Buyer - Guest'
                        }
                        roomCode={sale.viewerRoomCode}
                        activeStream={sale.activeStream}
                        sale={sale}
                      />
                    )}
                  </HMSRoomProvider>
                )}

                {sale.saleType === 'Timed' && (
                  <span style={{ fontSize: '1.4em', fontWeight: 'bold' }}>
                    Time Remaining: <TimeRemaining remainingMs={remainingMsForActiveLot} />
                  </span>
                )}
              </View>

              <Split />

              {announcements ? (
                <Accept
                  announcements={announcements}
                  buyerUserId={currentUser?.id}
                  setShowModal={setAnnouncements}
                />
              ) : (
                <Bids
                  key={`${activeSaleAsset?.id || 'na'}-${selectionTick}`}
                  saleAsset={activeSaleAsset}
                  sale={sale}
                  smallScreen={smallScreen}
                  remainingMs={remainingMsForActiveLot}
                />

              )}
            </BorderedContainer>

            <BorderedContainer style={{ marginTop: '10px' }}>
              {activeSaleAsset ? (
                <SaleAssetDetail
                  saleAsset={activeSaleAsset}
                  groupSaleAssets={sale.bidGroupSaleAssets}
                />
              ) : (
                <Title>No Assets Current Bidding</Title>
              )}
            </BorderedContainer>
          </Column>

          <SmallColumn>
            <SaleAssets sale={sale} updateSale={handleSelectSaleAsset} />
          </SmallColumn>

          {showTcs && (
            <Modal
              showModal
              modalType="Terms and Conditions"
              setShowModal={false}
              style={{ zIndex: '99999999', height: '300px' }}
            >
              <div style={{ maxHeight: '60VH' }}>
                You must agree to the Terms and Conditions before you can bid. Scroll to the bottom to
                approve.
                <Terms />
                <Pressable
                  style={{
                    backgroundColor: 'green',
                    padding: '15px',
                    borderRadius: '5px',
                    color: 'white',
                    fontWeight: 'bold',
                    align: 'center',
                  }}
                  onPress={handleTcsSubmit}
                >
                  I Agree to the Terms and Conditions
                </Pressable>
                <br />
                <br />
                <br />
              </div>
            </Modal>
          )}
        </Container>
      </BlackBackground>
    </>
  );
};

export default BiddingSale;
